<template>
  <div class="route-management">
    <!-- Header Section — always present, consistent hierarchy -->
    <div class="header-section">
      <div class="header-left">
        <h3 class="header-title">Route Management</h3>
        <span
          v-if="!loading"
          class="count-pill"
        >
          {{ routes.length }} {{ routes.length === 1 ? 'route' : 'routes' }}
        </span>
      </div>
      <v-btn
        v-if="!isAddingRoute"
        color="primary"
        variant="flat"
        size="small"
        class="add-route-btn"
        @click="openAddRoute"
      >
        <v-icon :icon="icons.mdiPlus" size="16" class="mr-1" />
        Add Route
      </v-btn>
    </div>

    <!-- Inline Add/Edit Route Form -->
    <v-expand-transition>
      <v-card
        v-if="isAddingRoute"
        class="add-route-form mb-4"
        variant="flat"
      >
        <v-card-text class="pa-5">
          <div class="form-header">
            <v-icon :icon="icons.mdiPlus" color="primary" size="20" class="me-2" />
            <span class="form-title">
              {{ selectedRoute ? 'Edit Route' : 'New Route' }}
            </span>
            <v-spacer />
            <v-btn
              :icon="icons.mdiClose"
              variant="text"
              size="small"
              @click="closeRouteForm"
            />
          </div>

          <v-form v-model="valid" @submit.prevent="saveRoute">
            <!-- Type — segmented chip group -->
            <div class="form-label">
              Type <span class="required-mark">*</span>
            </div>
            <div class="type-segment">
              <button
                v-for="opt in typeOptions"
                :key="opt.value"
                type="button"
                class="type-segment__btn"
                :class="{ 'type-segment__btn--active': form.type === opt.value }"
                @click="form.type = opt.value"
              >
                <v-icon :icon="opt.icon" size="16" />
                <span>{{ opt.title }}</span>
              </button>
            </div>

            <!-- Target field — shows only after type is chosen -->
            <div v-if="form.type" class="form-label">
              Target <span class="required-mark">*</span>
            </div>
            <v-select
              v-if="form.type === 's'"
              v-model="form.trunk_id"
              :items="trunks"
              item-title="name"
              item-value="id"
              placeholder="Choose a SIP trunk"
              :rules="[rules.sipTrunk]"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              class="form-field"
              :loading="loadingTrunks"
              :menu-props="{ contentClass: 'sip-trunk-menu' }"
            />
            <v-text-field
              v-else-if="form.type === 'e'"
              v-model="form.number"
              placeholder="+1234567890"
              :rules="[rules.externalNumber]"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              class="form-field"
            />
            <v-text-field
              v-else-if="form.type === 'u'"
              v-model="form.sip_uri"
              placeholder="sip:user@domain.com"
              :rules="[rules.sipUri]"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              class="form-field"
            />

            <!-- Description — visually de-emphasized as optional -->
            <div class="form-label form-label--optional">
              Description <span class="optional-mark">optional</span>
            </div>
            <v-text-field
              v-model="form.description"
              placeholder="What is this route for?"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              class="form-field"
            />

            <!-- Row 4: Actions -->
            <div class="form-actions">
              <v-btn
                variant="text"
                @click="closeRouteForm"
                :disabled="loading"
              >
                Cancel
              </v-btn>
              <v-btn
                type="submit"
                color="primary"
                variant="flat"
                :loading="loading"
                :disabled="!valid"
              >
                <v-icon :icon="icons.mdiCheck" size="16" class="mr-1" />
                {{ selectedRoute ? 'Save' : 'Create' }}
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <!-- Loading skeleton (shown until first successful load) -->
    <div v-if="!hasLoaded" class="loading-skeleton">
      <div v-for="i in 3" :key="i" class="skeleton-item">
        <div class="skeleton-icon"></div>
        <div class="skeleton-content">
          <div class="skeleton-line skeleton-line--md"></div>
          <div class="skeleton-line skeleton-line--sm"></div>
        </div>
      </div>
    </div>

    <!-- Routes List -->
    <div v-else-if="routes.length > 0" class="route-list">
      <div
        v-if="sortableHint"
        class="sortable-hint"
      >
        <v-icon :icon="icons.mdiDrag" size="14" class="mr-1" />
        Drag the handle to reorder. The first route receives the call first.
      </div>
      <div ref="routeListContainer" :key="`routes-list-${forceUpdate}`">
        <div
          class="route-item"
          v-for="route in routes"
          :key="`route-${route.id}-${forceUpdate}`"
          :data-id="route.id"
        >
          <div class="drag-handle-container" :title="'Drag to reorder'">
            <v-icon
              :icon="icons.mdiDrag"
              class="drag-handle"
            />
          </div>

          <div class="route-content">
            <div class="route-primary">
              <v-chip
                :color="getRouteTypeColor(route.type)"
                variant="tonal"
                size="small"
                class="route-chip"
              >
                {{ getRouteTypeText(route.type) }}
              </v-chip>
              <span class="route-value text-mono">{{ getRouteValue(route) }}</span>
            </div>
            <div v-if="route.description" class="route-description">
              {{ route.description }}
            </div>
          </div>

          <div class="route-actions">
            <v-btn
              :icon="icons.mdiPencil"
              variant="text"
              color="primary"
              size="small"
              :title="'Edit route'"
              @click="openEditRoute(route)"
            />
            <v-btn
              :icon="icons.mdiDelete"
              variant="text"
              color="error"
              size="small"
              :title="'Delete route'"
              @click="openDeleteRoute(route)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state" @click="openAddRoute">
      <div class="empty-state__icon">
        <v-icon :icon="icons.mdiPhoneForward" size="32" color="white" />
      </div>
      <div class="empty-state__title">No routes configured</div>
      <div class="empty-state__text">
        Routes define where incoming calls on this DID are sent.
        Click to add your first route.
      </div>
      <v-btn
        color="primary"
        variant="flat"
        class="empty-state__cta"
      >
        <v-icon :icon="icons.mdiPlus" size="16" class="mr-1" />
        Add your first route
      </v-btn>
    </div>

    <!-- Delete Confirmation Dialog — shows which route -->
    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card v-if="selectedRoute" class="delete-dialog">
        <div class="delete-dialog__icon">
          <v-icon :icon="icons.mdiAlertCircle" size="28" color="error" />
        </div>
        <v-card-title class="text-h6 pt-2">Delete this route?</v-card-title>
        <v-card-text class="text-body-2 text-medium-emphasis">
          You're about to permanently delete this route:
          <v-chip
            :color="getRouteTypeColor(selectedRoute.type)"
            variant="tonal"
            size="small"
            class="mx-1"
          >
            {{ getRouteTypeText(selectedRoute.type) }}
          </v-chip>
          <strong class="text-error text-mono">{{ getRouteValue(selectedRoute) }}</strong>
          <p v-if="selectedRoute.description" class="mt-2 mb-0 text-caption">
            "{{ selectedRoute.description }}"
          </p>
          <p class="mt-3 mb-0">
            Incoming calls on this DID will no longer reach this destination.
            This action cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn
            variant="text"
            @click="deleteDialog = false"
            :disabled="loading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="loading"
            @click="deleteRoute"
          >
            Delete route
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper'
import { mdiPlus, mdiClose, mdiDrag, mdiDelete, mdiPencil, mdiPhone, mdiWeb, mdiPhoneForward, mdiCheck, mdiAlertCircle, mdiServerNetwork, mdiDialpad, mdiLinkVariant } from '@mdi/js'
import Sortable from 'sortablejs'
import type { SortableEvent } from 'sortablejs'

const { success, error } = useSnackbar()
const props = defineProps<{
  didId: number
}>()

interface SipAccount {
  id: number
  name: string
  registration: boolean
  domain: string
  description: string
  sipIp: string | null
  sipPort: number
  maxCalls: number
  callsPerSecond: number
  outboundCallerIdNumber: string
  outboundCallerIdName: string
  enabled: boolean
  dateAdded: string
  dateModified: string
}

interface SipAccountsResponse {
  status: string
  message: string
  data: {
    count: number
    next: string | null
    previous: string | null
    results: SipAccount[]
  }
}

interface Route {
  id: number
  type: 's' | 'e' | 'u'
  trunk?: { id: number; name: string }
  number?: string
  sip_uri?: string
  description?: string
  order: number
}

interface RouteResponse {
  status: string;
  message: string;
  data: {
    id: number;
    type: 's' | 'e' | 'u';
    trunk?: number;
    number?: string;
    sipUri?: string;
    description?: string;
    order: number;
  };
}

const routes = ref<Route[]>([])
const trunks = ref<Array<{ id: number; name: string }>>([])
const loading = ref(false)
const loadingTrunks = ref(false)
const hasLoaded = ref(false)
const valid = ref(false)
const isAddingRoute = ref(false)
const deleteDialog = ref(false)
const selectedRoute = ref<Route | null>(null)
const forceUpdate = ref(0)
const sortableHint = ref(false)

const lastDidId = ref<number | null>(null);

const form = ref({
  type: '' as 's' | 'e' | 'u',
  trunk_id: null as number | null,
  number: '',
  sip_uri: '',
  description: ''
})

const icons = {
  mdiPlus, mdiClose, mdiDrag, mdiDelete, mdiPencil, mdiPhone, mdiWeb, mdiPhoneForward, mdiCheck, mdiAlertCircle, mdiServerNetwork, mdiDialpad, mdiLinkVariant
}

const typeOptions = [
  { title: 'SIP Trunk', value: 's' as const, icon: icons.mdiServerNetwork },
  { title: 'External', value: 'e' as const, icon: icons.mdiDialpad },
  { title: 'SIP URI', value: 'u' as const, icon: icons.mdiLinkVariant }
]

const rules = {
  required: (v: string) => !!v || 'Field is required',
  sipTrunk: (v: number | null) => {
    if (form.value.type === 's') {
      return !!v || 'SIP trunk is required';
    }
    return true;
  },
  externalNumber: (v: string) => {
    if (form.value.type === 'e') {
      return !!v || 'External number is required';
    }
    return true;
  },
  sipUri: (v: string) => {
    if (form.value.type === 'u') {
      return !!v || 'SIP URI is required';
    }
    return true;
  }
}

const routeListContainer = ref<HTMLElement | null>(null)
let sortableInstance: Sortable | null = null

const initSortable = () => {
  if (!routeListContainer.value) {
    return;
  }
  Array.from(routeListContainer.value.children).forEach((el, index) => {
    const route = routes.value[index];
    if (route) {
      el.setAttribute('data-id', route.id.toString());
    }
  });
  sortableInstance = Sortable.create(routeListContainer.value, {
    handle: '.drag-handle-container',
    animation: 150,
    ghostClass: 'route-ghost',
    onStart: () => {
      sortableHint.value = true;
    },
    onEnd: (evt: SortableEvent) => {
      const newOrder = Array.from(routeListContainer.value?.children || []).map(el => {
        const id = Number(el.getAttribute('data-id'));
        return routes.value.find(route => route.id === id);
      }).filter(Boolean) as typeof routes.value;
      routes.value = newOrder;
      sortableHint.value = false;
      onDragEnd();
    }
  });
}

const destroySortable = () => {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
}

const getRouteTypeText = (type: string) => {
  switch (type) {
    case 's': return 'SIP Trunk';
    case 'e': return 'External';
    case 'u': return 'SIP URI';
    default: return type;
  }
}

const getRouteTypeColor = (type: string) => {
  switch (type) {
    case 's': return 'primary';
    case 'e': return 'secondary';
    case 'u': return 'info';
    default: return 'grey';
  }
}

// Get the displayable value for a route (the actual target/endpoint)
const getRouteValue = (route: Route): string => {
  if (route.type === 's') return route.trunk?.name || 'Unknown trunk';
  if (route.type === 'e') return route.number || '';
  if (route.type === 'u') return route.sip_uri || '';
  return '';
};

// Trunks fetched once per DID
const fetchTrunks = async (force = false) => {
  if (loadingTrunks.value) return;
  if (!force && trunks.value.length > 0) return;
  loadingTrunks.value = true;
  try {
    const response = await fetchWrapper.get(`${import.meta.env.VITE_API_URL}/sipaccounts/`);
    if (response.status === 'success' && response.data) {
      const data = response.data as { results: Array<{ id: number; name: string }> };
      trunks.value = data.results.map(trunk => ({ id: trunk.id, name: trunk.name }));
    } else {
      throw new Error('Invalid response format');
    }
  } catch (err: unknown) {
    error({ text: 'Failed to fetch SIP trunks' });
  } finally {
    loadingTrunks.value = false;
  }
}

const fetchRoutes = async () => {
  if (lastDidId.value === props.didId && routes.value.length > 0) return;
  if (trunks.value.length === 0) {
    await fetchTrunks();
  }
  loading.value = true;
  try {
    const response = await fetchWrapper.get(`${import.meta.env.VITE_API_URL}/numbers/${props.didId}/routes/`);
    const routesData = response.data as Array<{
      id: number;
      type: 's' | 'e' | 'u';
      trunk?: number;
      number?: string;
      sipUri?: string;
      description?: string;
      order: number;
    }>;
    lastDidId.value = props.didId;
    routes.value = routesData.map(route => {
      const mappedRoute: Route = {
        id: route.id,
        type: route.type,
        number: route.number,
        sip_uri: route.sipUri,
        description: route.description,
        order: route.order,
        trunk: undefined
      };
      if (route.type === 's' && route.trunk) {
        const trunk = trunks.value.find(t => t.id === route.trunk);
        if (trunk) {
          mappedRoute.trunk = { id: trunk.id, name: trunk.name };
        }
      }
      return mappedRoute;
    });
    // Wait for Vue to actually mount the v-else-if block (skeleton/empty are gone)
    // Two RAFs guarantee both ref binding and child render completed
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        destroySortable();
        initSortable();
      });
    });
  } catch (err: unknown) {
    error({ text: 'Failed to fetch routes' });
  } finally {
    hasLoaded.value = true;
    loading.value = false;
  }
}

const openAddRoute = async () => {
  selectedRoute.value = null;
  form.value = {
    type: '' as 's' | 'e' | 'u',
    trunk_id: null,
    number: '',
    sip_uri: '',
    description: ''
  };
  isAddingRoute.value = true;
  // Refresh trunks once (in case they were added/renamed elsewhere)
  await fetchTrunks(true);
}

const openEditRoute = (route: Route) => {
  selectedRoute.value = route;
  form.value = {
    type: route.type,
    trunk_id: route.trunk?.id || null,
    number: route.number || '',
    sip_uri: route.sip_uri || '',
    description: route.description || ''
  };
  isAddingRoute.value = true;
}

const closeRouteForm = () => {
  isAddingRoute.value = false;
  selectedRoute.value = null;
}

const saveRoute = async () => {
  if (!valid.value) return;
  loading.value = true;
  try {
    const routeData = {
      type: form.value.type,
      trunk_id: form.value.type === 's' ? form.value.trunk_id : undefined,
      number: form.value.type === 'e' ? form.value.number : undefined,
      sip_uri: form.value.type === 'u' ? form.value.sip_uri : undefined,
      description: form.value.description
    };
    if (selectedRoute.value) {
      await fetchWrapper.patch(
        `${import.meta.env.VITE_API_URL}/numbers/${props.didId}/routes/${selectedRoute.value.id}/`,
        routeData
      );
      success({ text: 'Route updated successfully' });
    } else {
      await fetchWrapper.post(
        `${import.meta.env.VITE_API_URL}/numbers/${props.didId}/routes/create/`,
        routeData
      );
      success({ text: 'Route added successfully' });
    }
    lastDidId.value = null;
    await fetchRoutes();
    closeRouteForm();
  } catch (err: unknown) {
    error({ text: 'Failed to save route' });
  } finally {
    loading.value = false;
  }
}

const openDeleteRoute = (route: Route) => {
  selectedRoute.value = route;
  deleteDialog.value = true;
}

const deleteRoute = async () => {
  if (!selectedRoute.value) return;
  loading.value = true;
  try {
    const routeId = selectedRoute.value.id;
    await fetchWrapper.delete(
      `${import.meta.env.VITE_API_URL}/numbers/${props.didId}/routes/${routeId}/delete/`
    );
    routes.value = routes.value.filter(r => r.id !== routeId);
    success({ text: 'Route deleted successfully' });
    nextTick(() => {
      destroySortable();
      initSortable();
    });
  } catch (err: unknown) {
    error({ text: 'Failed to delete route' });
  } finally {
    loading.value = false;
    deleteDialog.value = false;
    selectedRoute.value = null;
  }
}

const onDragEnd = async () => {
  loading.value = true;
  try {
    await fetchWrapper.post(
      `${import.meta.env.VITE_API_URL}/numbers/${props.didId}/routes/reorder/`,
      { ordered_ids: routes.value.map(route => route.id) }
    );
    success({ text: 'Routes reordered successfully' });
  } catch (err: unknown) {
    error({ text: 'Failed to reorder routes' });
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchRoutes();
})

// Refetch routes when parent switches to a different DID
watch(
  () => props.didId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      hasLoaded.value = false;
      routes.value = [];
      fetchRoutes();
    }
  }
)

onUnmounted(() => {
  destroySortable();
})
</script>

<style scoped lang="scss">
.route-management {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* ============================================
   Header — consistent hierarchy
   ============================================ */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  margin: 0;
  letter-spacing: -0.01em;
}

.count-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  background: rgba(var(--v-theme-primary), 0.10);
  color: rgb(var(--v-theme-primary));
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.4;
}

.add-route-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
}

/* ============================================
   Form
   ============================================ */
.add-route-form {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-borderLight), 0.8);
  border-radius: 12px !important;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 4px 12px -2px rgba(0, 0, 0, 0.06);
}

.form-header {
  display: flex;
  align-items: center;
  margin: -20px -20px 20px;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.06) 0%,
    rgba(var(--v-theme-accent), 0.04) 100%
  );
  border-bottom: 1px solid rgba(var(--v-theme-borderLight), 0.6);
  border-radius: 12px 12px 0 0;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

.form-field {
  margin-bottom: 12px;
  font-size: 0.875rem;
}

.form-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 6px;
  margin-top: 4px;
}

.form-label--optional {
  font-weight: 500;
  color: rgb(var(--v-theme-lightText));
}

.required-mark {
  color: rgb(var(--v-theme-error));
  margin-left: 2px;
}

.optional-mark {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  color: rgb(var(--v-theme-lightText));
  margin-left: 6px;
  font-size: 0.6875rem;
}

.type-segment {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 4px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 8px;
  margin-bottom: 12px;
}

.type-segment__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface-variant));
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(.type-segment__btn--active) {
    background: rgba(var(--v-theme-on-surface), 0.04);
    color: rgb(var(--v-theme-on-surface));
  }

  &--active {
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-primary));
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(var(--v-theme-primary), 0.10);
    font-weight: 600;
  }

  &:focus-visible {
    outline: 2px solid rgba(var(--v-theme-primary), 0.5);
    outline-offset: 2px;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-borderLight), 0.4);
}

/* ============================================
   Loading skeleton
   ============================================ */
.loading-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgb(var(--v-theme-surface-light));
  border-radius: 8px;
  align-items: center;
}

.skeleton-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(var(--v-theme-on-surface), 0.10);
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-line {
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-surface), 0.06) 0%,
    rgba(var(--v-theme-on-surface), 0.12) 50%,
    rgba(var(--v-theme-on-surface), 0.06) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;

  &--md { width: 40%; }
  &--sm { width: 25%; }
}

@keyframes skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ============================================
   Sortable hint
   ============================================ */
.sortable-hint {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 8px;
  background: rgba(var(--v-theme-primary), 0.06);
  border: 1px dashed rgba(var(--v-theme-primary), 0.25);
  border-radius: 6px;
  font-size: 0.75rem;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

/* ============================================
   Route list
   ============================================ */
.route-list {
  flex: 1;
  overflow-y: auto;
  background: transparent;
}

.route-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-borderLight), 0.6);
  border-radius: 8px;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
  user-select: none;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.3);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  &.route-ghost {
    opacity: 0.5;
    background: rgba(var(--v-theme-primary), 0.08);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  }
}

.drag-handle-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 8px;
  margin-left: -8px;
  cursor: move;
  border-radius: 8px;
  transition: background-color 0.15s ease;
  flex-shrink: 0;
  touch-action: none;

  &:hover {
    background: rgba(var(--v-theme-primary), 0.08);
  }

  &:active {
    background: rgba(var(--v-theme-primary), 0.16);
  }
}

.drag-handle {
  cursor: move;
  opacity: 0.6;
  transition: opacity 0.15s ease;
  font-size: 22px;

  &:hover {
    opacity: 1;
  }
}

.route-item:hover .drag-handle {
  opacity: 0.9;
}

.route-content {
  flex: 1;
  min-width: 0;
}

.route-primary {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.route-chip {
  flex-shrink: 0;
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  height: 22px;
}

.route-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.route-description {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-lightText));
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.route-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 8px;
}

.text-mono {
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace;
}

/* ============================================
   Empty state — clickable, prominent CTA
   ============================================ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: rgb(var(--v-theme-surface));
  border: 1px dashed rgba(var(--v-theme-borderLight), 0.8);
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.4);
    background: rgba(var(--v-theme-primary), 0.02);
  }
}

.empty-state__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 8px 20px -6px rgba(var(--v-theme-primary), 0.4);
}

.empty-state__title {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 6px;
}

.empty-state__text {
  font-size: 0.8125rem;
  color: rgb(var(--v-theme-lightText));
  max-width: 320px;
  margin-bottom: 16px;
  line-height: 1.5;
}

.empty-state__cta {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
}

/* ============================================
   Delete dialog
   ============================================ */
.delete-dialog {
  border-radius: 14px !important;
  padding-top: 24px;
}

.delete-dialog__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(var(--v-theme-error), 0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
}
</style>
